import * as React from "react";
import { Alert, AlertTitle, Card, CardHeader } from "@mui/material";
import { getErrorMsg } from "../../../../utilities/rtkQueryErrorHelpers";
import { useSearchJournalEntriesMutation } from "../../../../services/cclTokenedJournalEntryApi";
import { JournalEntry } from "../../../../services/types/journalEntryTypes";
import ComponentLoader from "../../../../components/common/componentLoader";
import HistoryTimeLine from "../../../../components/common/historyTimeline/historyTimeline";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import {
    UserClaimsService,
    eGroupIds,
} from "../../../../services/currentUserService/currentUserService";
import SessionJournalDrawer from "../sessionJournalDrawer";

interface SessionJournalEntryTimelineCardProps {
    sessionCode: string;
    sessionId: string;
    drawerOpen: boolean;
}

const SessionJournalEntryTimelineCard: React.FC<SessionJournalEntryTimelineCardProps> = (props) => {
    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(props.drawerOpen);
    const [allJournalEntries, setAllJournalEntries] = React.useState<JournalEntry[]>([]);
    const [
        runSearchJournalEntries,
        {
            isLoading: journalEntriesLoading,
            isError: journalEntriesIsError,
            error: journalEntriesError,
        },
    ] = useSearchJournalEntriesMutation();
    const claimsService = new UserClaimsService();
    const allowJournalEntries = claimsService.IsUserInGroups([
        eGroupIds.eClientAdmin,
        eGroupIds.eNWAAdmin,
        eGroupIds.eCCLAdmin,
        eGroupIds.eCCLSuperAdmin,
    ]);

    React.useEffect(() => {
        if (props.drawerOpen === drawerOpen) return;
        setDrawerOpen(props.drawerOpen);
        //eslint-disable-next-line
    }, [props.drawerOpen]);

    const memoizedSearchJournal = React.useCallback(
        (fieldName: string, fieldValue: string) => {
            runSearchJournalEntries({
                journalCategoryIds: [2], // session journal entries
                categoryDataFieldName: fieldName,
                categoryDataFieldValue: fieldValue,
            })
                .unwrap()
                .then((journalentries: JournalEntry[]) => {
                    setAllJournalEntries([...journalentries]);
                })
                .catch(() => {
                    setAllJournalEntries([]);
                });
        },
        [runSearchJournalEntries]
    );

    React.useEffect(() => {
        if (props.sessionCode !== "") {
            memoizedSearchJournal("sessionCode", props.sessionCode);
        } else if (props.sessionId !== "") {
            memoizedSearchJournal("sessionId", props.sessionId);
        }
    }, [props.sessionCode, props.sessionId, memoizedSearchJournal]);

    function showErrorAlert(msg: string | undefined) {
        return (
            <div>
                <Alert severity="error" sx={{ fontSize: 14 }}>
                    <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                    {msg}
                </Alert>
            </div>
        );
    }

    if (journalEntriesIsError) {
        return showErrorAlert(getErrorMsg(journalEntriesError));
    }

    const onJournalEntrySaved = () => {
        if (props.sessionCode !== "") {
            memoizedSearchJournal("sessionCode", props.sessionCode);
        } else if (props.sessionId !== "") {
            memoizedSearchJournal("sessionId", props.sessionId);
        }
        setDrawerOpen(false);
    };

    return (
        <React.Fragment>
            {allowJournalEntries && (
                <SessionJournalDrawer
                    sessionId={props.sessionId}
                    sessionCode={props?.sessionCode ?? ""}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    onSave={onJournalEntrySaved}
                />
            )}
            <Card sx={{ height: 1, width: { xs: 1, lg: 0.8, xl: 0.6 }, m: 2, p: 2 }}>
                {allowJournalEntries ? (
                    <CardHeader
                        action={
                            <CclButton mode={"primary"} onClick={() => setDrawerOpen(true)}>
                                Create Journal Entry
                            </CclButton>
                        }
                    />
                ) : null}
                {journalEntriesLoading ? (
                    <ComponentLoader msg="Retrieving Journal Entries..." />
                ) : (
                    <HistoryTimeLine events={[]} journalEntries={allJournalEntries} />
                )}
            </Card>
        </React.Fragment>
    );
};

export default SessionJournalEntryTimelineCard;
